import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { Provider, ProviderControllerApi } from "@/services";
import {
  DGT_Pagination,
  asPaginatedContent,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  useBaseContent,
} from "@/utils/utils";

/** The endpoint sends a lightweight provider: uuid, email, firstName, lastName. */
export type CoSigner = Required<Pick<Provider, "uuid">> &
  Pick<Provider, "email" | "firstName" | "lastName">;

export interface UseGetCosignersProps {
  pagination: DGT_Pagination;
  searchString?: string;
  xTENANTID?: string;
  enabled?: boolean;
}

/** Active Supervising MD providers who can co-sign. */
export function useGetCosigners({
  pagination,
  searchString,
  xTENANTID,
  enabled = true,
}: UseGetCosignersProps): useBaseContent<CoSigner> {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey: searchString,
    prevPage,
  });
  const pageSize = getPageSize(searchString, pagination);

  return useQuery({
    queryKey: [
      "provider-cosigners",
      pageSize,
      pageIndex,
      searchString,
      xTENANTID,
    ],
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: !!xTENANTID && enabled,
    queryFn: async () =>
      new ProviderControllerApi(getConfigObject())
        .getCosigners(pageIndex, pageSize, searchString || undefined, xTENANTID)
        .then((res) => asPaginatedContent<CoSigner>(res?.data)),
  });
}
